import { t } from "i18next";
// import { Input } from "antd";
import DateInput from "components/common/Input/DateInput";
import { getSettings } from "store/slices";
import { useAppSelector } from "store/hooks";
import { useMemo } from "react";
import CustomSelect from "components/common/Input/CustomSelect";
import citiesList from "assets/jsons/cities.json";
import { validateDateInput, sanitizeTextInput } from "utils/security";

export default function FirstStep({
  formState,
  setFormState,
  ageWarning,
  computeAgeWarning,
}) {
  const { dir, language } = useAppSelector(getSettings);
  const maxDate = useMemo(() => String(new Date().getFullYear() - 16), []);

  return (
    <div className="w-full" dir={dir}>

      {/* Form Content */}
      <div className="space-y-4 md:space-y-6">
        
        {/* Personal Information Section */}
        <div className="space-y-3 md:space-y-4">


          {/* Personal Information Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" dir={dir}>
              <DateInput
                label=""
                id="date_of_birth"
                type="date"
                value={formState.data.date_of_birth}
                onChange={(value) => {
                  // Convert number to string for validation
                  const stringValue = String(value);
                  const validation = validateDateInput(stringValue);
                  if (validation.isValid) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        date_of_birth: validation.sanitized,
                      },
                    }));
                    computeAgeWarning(validation.sanitized);
                  }
                }}
                placeholder={t("registerDOB")}
                min="1950-01-01"
                note={ageWarning}
                noteClass="text-amber-500 text-xs mt-1"
                dir={dir}
                picker="year"
                dateFormat="YYYY"
                maxDate={maxDate}
              />
            

            
              <CustomSelect
                onChange={(value) => {
                  const sanitizedValue = sanitizeTextInput(value);
                  if (sanitizedValue.length > 0) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        gender: sanitizedValue,
                      },
                    }));
                  }
                }}
                value={formState.data.gender}
                options={[
                  {
                    value: "male",
                    label: t("signupMale"),
                  },
                  {
                    value: "female",
                    label: t("signupFemale"),
                  },
                ]}
                placeholder={t("signupGenderPlaceholder")}
              />
            

            <div className="md:col-span-2">
              <CustomSelect
                onChange={(value, {label}) => {
                  const sanitizedLabel = sanitizeTextInput(label);
                  if (sanitizedLabel.length > 0) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        city: sanitizedLabel,
                      },
                    }));
                  }
                }}
                value={formState.data.city}
                options={citiesList.map((city) => ({
                  value: city.id,
                  label: city[language],
                }))}
                placeholder={t("signupCityPlaceholder")}
                searchOption={true}
              />
            </div>
          </div>
        </div>

        {/* Looking For Section */}
        <div className="space-y-3 md:space-y-4">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">

            <h2 className="text-base md:text-lg font-semibold text-gray-900">
              {t("signupLookingForInformation")}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-2 md:gap-3">
            {/* Majors Option */}
            <label 
              className={`relative flex items-center p-2.5 md:p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-sm ${
                formState.data.looking_for === "majors" 
                  ? "border-blue-500 bg-blue-50 shadow-sm" 
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="looking_for"
                value="majors"
                className="sr-only"
                checked={formState.data.looking_for === "majors"}
                onChange={(e) => {
                  const sanitizedValue = sanitizeTextInput(e.target.value);
                  if (sanitizedValue.length > 0) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        looking_for: sanitizedValue,
                      },
                    }));
                  }
                }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">{t("signupMajors")}</h3>
                    <p className="text-gray-600 text-xs md:text-sm">{t("iWantInformationAboutMajors")}</p>
                  </div>
                </div>
              </div>
            </label>

            {/* Careers Option */}
            <label 
              className={`relative flex items-center p-2.5 md:p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-sm ${
                formState.data.looking_for === "careers" 
                  ? "border-blue-500 bg-blue-50 shadow-sm" 
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="looking_for"
                value="careers"
                className="sr-only"
                checked={formState.data.looking_for === "careers"}
                onChange={(e) => {
                  const sanitizedValue = sanitizeTextInput(e.target.value);
                  if (sanitizedValue.length > 0) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        looking_for: sanitizedValue,
                      },
                    }));
                  }
                }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">{t("signupCareers")}</h3>
                    <p className="text-gray-600 text-xs md:text-sm">{t("iWantInformationAboutCareers")}</p>
                  </div>
                </div>
              </div>
            </label>

            {/* Exploring Options */}
            <label 
              className={`relative flex items-center p-2.5 md:p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-sm ${
                formState.data.looking_for === "explorign-future-options" 
                  ? "border-blue-500 bg-blue-50 shadow-sm" 
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="looking_for"
                value="explorign-future-options"
                className="sr-only"
                checked={formState.data.looking_for === "explorign-future-options"}
                onChange={(e) => {
                  const sanitizedValue = sanitizeTextInput(e.target.value);
                  if (sanitizedValue.length > 0) {
                    setFormState((pre) => ({
                      ...pre,
                      data: {
                        ...pre.data,
                        looking_for: sanitizedValue,
                      },
                    }));
                  }
                }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">{t("signupExploringOptions")}</h3>
                    <p className="text-gray-600 text-xs md:text-sm">{t("iWantInformationAboutNotSure")}</p>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
